// EPG channel-name normalizer. Used by `useEpgStore` (client-side fallback
// index) and mirrored at `server/services/epgMatch.js` (catchup-EPG fallback).
// Both implementations must produce byte-identical output for the same input.
//
// SCHEMA VERSION: 2 (post-4.10 normalizer extension — country prefix preserved).
//
// Phase A (post-4.10) shipped the original normalizer: pipe-form region prefix,
// ASCII codec/quality subset (HD/FHD/UHD/SD/4K/8K/720p/1080p/2160p), pictographs +
// regional-indicator emoji, basic punctuation. It bridged the BBC One spelling
// drift but missed every channel decorated with Unicode small-caps / superscripts
// (`ᴴᴰ`, `ᵁᴴᴰ`, `ᴿᴬᵂ`), `*MULTI*` markers, `(D)` / `(H)` quality codes, hash-wrapped
// names, codec tags (`HEVC`, `H265`, `RAW`), or geometric-bullet decorations.
//
// SCHEMA VERSION 2 (this file): superset of v1. Idempotent over v1 inputs (v1
// outputs hash to the same v2 output — the test suite has the byte-equality
// fixture). The four-tier resolution model that consumes this key:
//
//   1. tvg-id strict match (caller's responsibility — unchanged from v1).
//   2. Strict-prefix match: `${livePrefix}:${normalizeChannelName(nameAfterColon)}`.
//      The country prefix is PRESERVED through the normalizer so the resolver
//      can do exact-prefix matching against the EPG-side prefixed key.
//   3. Alias-prefix match: for each alias in `expandPrefixAliases(livePrefix)`,
//      try `${alias}:${strippedKey}`. Aliases live in `epgAliases.ts`.
//   4. N=1 bare-name fallback: lookup the bare stripped key against ALL EPG
//      channels (across all enabled sources). If EXACTLY ONE channel matches
//      AND has a programme covering the requested timestamp, return it.
//      Multi-candidate (N>1) cases skip silently — the Phase C per-channel
//      override is the user-facing escape hatch.
//
// COUNTRY PREFIX IS NOT STRIPPED INSIDE THE NORMALIZER. That's load-bearing:
// the empirical evidence (DAZN 1, QVC, LA 1) shows different countries' channels
// share the same stripped key but carry DIFFERENT content (Spanish on `ES:`, German
// on `SKYGO:`, English on `US:`). Pre-stripping would mis-bridge these cases.
// The colon-form strip happens ONLY in the tier-3 N=1 fallback layer of the
// caller (see `web/src/stores/epg.ts::_rebuildIndex`).
//
// `normalizeChannelName` itself does NOT preserve `4K:` and `8K:` country
// prefixes in its output — those tokens are also valid quality decorators
// (`BBC ONE 4K`) and the bare-codec strip handles both positions uniformly.
// Production callers always split the prefix off the raw input FIRST via
// `extractCountryPrefix` + `normalizeChannelNameWithoutPrefix` (the helper
// pair below), so the prefixed key shape is `${rawPrefix}:${bareKey}` rather
// than `normalizeChannelName(rawNameIncludingPrefix)`. Calling
// `normalizeChannelName('4K: SKY SPORTS F1')` directly yields
// `'sky sports f1'` — by design.
//
// Known limitations (consumers handle via Phase C `custom_epg_channel_id`):
//
//   - Plural/singular drift: `SKY SPORTS MAIN EVENTS` ≠ `SKY SPORTS MAIN EVENT`.
//     Damerau-Levenshtein / English stemming is out of scope (false-positive risk).
//   - Mid-word glyph substitution: `SP⚽RTS` ≠ `SPORTS`. The pictograph strip
//     only collapses standalone glyphs; substituting `⚽`→`o` would over-bridge.
//   - Number-spelling drift: `BBC One` ≠ `BBC1`. Per-network alias table is a
//     separate follow-up phase.
//
// Pair contract: this file and `server/services/epgMatch.js` share the SAME
// schema version + identical docstring. The pair-contract byte-equality test
// (`tests/unit/epgMatchPair.test.js`) fails the build if either drifts.

/**
 * Normalize a channel name for fuzzy EPG matching.
 *
 * Steps (each idempotent so `f(f(x)) === f(x)`):
 *
 *   1. Lowercase.
 *   2. Hash-wrap strip — `^#+\s*` and `\s*#+$` (asymmetric counts allowed).
 *   3. Asterisk-wrapped markers — `\*[a-z0-9]+\*` → space (e.g. `*MULTI*`).
 *   4. Parenthetical single-letter quality codes — `\b\([a-z]\)\b` → space
 *      (e.g. `(D)`, `(H)`, `(A)`).
 *   5. Pipe-form region prefix — `^[a-z0-9]{2,4}\s*\|\s*` (legacy Phase A).
 *   6. Codec / quality decorators, bracketed AND bare:
 *      `fhd|uhd|sd|hd|4k|8k|720p|1080p|2160p|hevc|h265|h264|avc|raw`.
 *   7. Unicode block strip — iterates `charCodeAt` numeric ranges (per
 *      learnings.md Phase A retro #1 — NEVER raw control bytes in a regex):
 *        - `U+02B0..U+02FF` Spacing Modifier Letters (`ʰ`)
 *        - `U+1D00..U+1D7F` Phonetic Extensions (`ᴴ ᴰ ᴬ ᴿ ᵁ ᵂ ᴵ ᴾ ᵉ ᵛ`)
 *        - `U+1D80..U+1DBF` Phonetic Extensions Supplement (`ᶜ ᶠ`)
 *        - `U+2070..U+209F` Superscripts and Subscripts (`⁰ ⁴ ⁸`)
 *        - `U+00B2..U+00B3` Latin-1 superscript digits (`² ³`)
 *        - `U+2C60..U+2C7F` Latin Extended-C (`ⱽ` at U+2C7D)
 *        - `U+25A0..U+25FF` Geometric Shapes (`◉`)
 *   8. Pictographs + regional indicators — Phase A behaviour preserved.
 *   9. Punctuation `[-_:|()\[\]/.,]` → space.
 *  10. Whitespace collapse + trim.
 *
 * Returns the empty string for null/undefined/non-string/empty input. Pure
 * function — no I/O, no global state, no log, no Pinia.
 *
 * COUNTRY PREFIX IS PRESERVED. See the file-header comment for the rationale.
 *
 * @see ./epgAliases.ts — the alias map consumed by tier-3 of the resolver.
 */
export function normalizeChannelName(name: unknown): string {
    if (typeof name !== 'string' || name.length === 0) return '';
    let s = name.toLowerCase();

    // Hash-wrap (leading + trailing).
    s = s.replace(/^#+\s*/, '');
    s = s.replace(/\s*#+$/, '');

    // Asterisk-wrapped markers + parenthetical single-letter codes.
    // No \b around \([a-z]\) — JS \b sits between word/non-word chars, and
    // both `(` and the preceding ` ` are non-word, so the boundary doesn't
    // fire. Plain `\([a-z]\)` is sufficient because the only legitimate
    // shape we want to strip is a standalone single-letter parenthetical;
    // multi-letter forms like `(BR)` survive (the punctuation step then
    // demotes the parens to spaces, leaving the letters intact).
    s = s.replace(/\*[a-z0-9]+\*/g, ' ');
    s = s.replace(/\([a-z]\)/g, ' ');

    // Pipe-form region prefix (legacy Phase A). Country prefix (colon-form)
    // is intentionally NOT stripped here — see file header.
    s = s.replace(/^[a-z0-9]{2,4}\s*\|\s*/, '');

    // Codec / quality decorators, bracketed AND bare. The bare alternation
    // strips `4k` / `8k` regardless of position. That's intentional even
    // though those tokens DOUBLE as country prefixes in the live playlist
    // (`4K: SKY SPORTS F1`) — the prefix-vs-codec ambiguity is resolved at
    // the CONSUMER layer, not here: every site that builds a prefixed key
    // calls `extractCountryPrefix` + `normalizeChannelNameWithoutPrefix`
    // (the helper pair below) so the prefix is split off the raw input
    // BEFORE `normalizeChannelName` runs on the bare remainder. Trying to
    // disambiguate inside this function (e.g. via a lookahead for `:`)
    // breaks idempotence — `normalizeChannelName` collapses `4K:` to ` `
    // in the colon-strip step downstream, after which the lookahead can no
    // longer fire.
    s = s.replace(
        /[(\[]\s*(?:fhd|uhd|sd|hd|4k|8k|720p|1080p|2160p|hevc|h265|h264|avc|raw)\s*[)\]]/g,
        ' ',
    );
    s = s.replace(
        /\b(?:fhd|uhd|sd|hd|4k|8k|720p|1080p|2160p|hevc|h265|h264|avc|raw)\b/g,
        ' ',
    );

    // Unicode block strip via charCodeAt numeric ranges. Never bake raw
    // control bytes into a regex character class (Phase A retro #1).
    let out = '';
    for (let i = 0; i < s.length; i++) {
        const code = s.charCodeAt(i);
        const stripped =
            (code >= 0x02b0 && code <= 0x02ff)
            || (code >= 0x1d00 && code <= 0x1d7f)
            || (code >= 0x1d80 && code <= 0x1dbf)
            || (code >= 0x2070 && code <= 0x209f)
            || code === 0x00b2
            || code === 0x00b3
            || (code >= 0x2c60 && code <= 0x2c7f)
            || (code >= 0x25a0 && code <= 0x25ff);
        out += stripped ? ' ' : s[i];
    }
    s = out;

    // Pictographs + regional indicators (Phase A).
    s = s.replace(/[\p{Extended_Pictographic}\p{Regional_Indicator}]/gu, ' ');

    // Punctuation. `*` and `#` join the standard set so that lone fragments
    // surviving the dedicated asterisk-marker / hash-wrap rules above
    // (e.g. an unmatched `*` mid-name, or a hash buried in punctuation that
    // the leading/trailing strip didn't see) demote to whitespace instead of
    // leaking into the key.
    s = s.replace(/[\-_:|()\[\]/.,#*]/g, ' ');

    // Whitespace collapse + trim.
    s = s.replace(/\s+/g, ' ').trim();
    return s;
}

/**
 * Extract the country/region prefix from a raw channel name (BEFORE any
 * normalization). Returns the uppercased prefix or `null` when the name
 * has no colon-form prefix.
 *
 * Examples: `"DE: DAZN 1 ᴿᴬᵂ"` → `"DE"`, `"4K: SKY SPORTS F1"` → `"4K"`,
 * `"BBC One"` → `null`, `"PLAY+: NPO 1"` → `"PLAY+"`.
 *
 * Tolerant of arbitrary surrounding whitespace. Strict 2..8 character cap
 * matches the cardinality of real-world prefixes the user's playlist carries
 * (CG, NM, MEO, JOYN, PLAY+, BE-VIP, etc.). Returning the prefix lets the
 * caller key the strict-prefix index AND drive `expandPrefixAliases` without
 * having to redo the regex.
 */
export function extractCountryPrefix(name: unknown): string | null {
    if (typeof name !== 'string' || name.length === 0) return null;
    const m = name.match(/^\s*([A-Za-z0-9/+\-]{2,8})\s*:\s*/);
    if (!m) return null;
    return (m[1] ?? '').toUpperCase();
}

/**
 * Strip the country/region colon-form prefix from a raw channel name and
 * normalize the remainder. Used by the tier-3 N=1 bare-name fallback to
 * produce a stripped-key that doesn't carry the country.
 *
 * `"DE: DAZN 1 ᴿᴬᵂ"` → `"dazn 1"`; `"BBC One"` → `"bbc one"`.
 */
export function normalizeChannelNameWithoutPrefix(name: unknown): string {
    if (typeof name !== 'string' || name.length === 0) return '';
    const stripped = name.replace(/^\s*[A-Za-z0-9/+\-]{2,8}\s*:\s*/, '');
    return normalizeChannelName(stripped);
}
